/**
 * 後台 — 重複帳號偵測(整理會員資料)
 *
 * 以「手機」或「姓名」分組,同組 ≥ 2 筆視為疑似重複。
 * 點任一帳號 → 會員 360 頁 →「帳號整理」→ 合併 / 刪除。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminListDuplicateMembers, type DuplicateGroup } from "../../api/admin";
import { TIER_LABEL } from "../../components/TierBadge";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


export default function MemberDuplicates() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await adminListDuplicateMembers();
        setGroups(r.groups);
      } catch {
        useUIStore.getState().showToast("載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [role, navigate]);

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/admin/members")} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">重複帳號偵測</h1>
      </header>

      <div className="px-6 mt-6 max-w-3xl space-y-4">
        <div className="text-sm text-muted">
          以「手機」或「姓名」分組,同組 ≥ 2 筆視為疑似重複。點任一帳號進去 →「帳號整理」→ 合併到要保留的那個。
        </div>

        {loading ? (
          <div className="text-muted">載入中…</div>
        ) : groups.length === 0 ? (
          <div className="card text-muted text-sm">沒有偵測到重複帳號</div>
        ) : (
          <>
            <div className="text-xs text-muted">共 {groups.length} 組疑似重複</div>
            {groups.map((g, i) => (
              <div key={g.type + g.key + i} className="card space-y-3">
                <div className="text-sm flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      g.type === "phone" ? "bg-sage text-white" : "bg-sage-light text-charcoal"
                    }`}
                  >
                    {g.type === "phone" ? "同手機" : "同姓名"}
                  </span>
                  <span className="font-medium text-ink">{g.key}</span>
                  <span className="text-muted text-xs">{g.members.length} 筆</span>
                </div>
                <div className="divide-y divide-line">
                  {g.members.map((mm) => (
                    <button
                      key={mm.line_user_id}
                      onClick={() => navigate(`/admin/members/${mm.line_user_id}`)}
                      className="w-full text-left py-2 hover:bg-cream/60 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{mm.name || "(無名)"}</div>
                        <div className="text-xs text-muted">
                          {mm.phone || "無電話"} · {TIER_LABEL[mm.tier as keyof typeof TIER_LABEL] || mm.tier}
                          {" · "}訂單 {mm.total_orders}
                          {mm.source === "manual" ? " · 手動建" : ""}
                        </div>
                        <div className="text-xxs text-muted font-mono truncate">{mm.line_user_id}</div>
                      </div>
                      <span className="text-primary text-xs shrink-0">查看 ›</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
